import { Dataset } from "@/components/ui/icons";
import { DatasetGroup } from "@/queries/datasets/types";
import { keepRelevantSearchParams } from "@/utils/utils";
import {
  CalendarIcon,
  CollectionIcon,
  TableIcon,
} from "@heroicons/react/outline";
import moment from "moment";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardVariant } from "../ui/Cards/Card";
import { CardTagsSeparator } from "../ui/Cards/Card/CardTags";
import { BasicTooltip } from "../ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import { TrashIcon } from "@heroicons/react/outline";
import { useDeleteDatasetGroup } from "@/queries/datasets/useDeleteDatasetGroup";
import { DropdownMenuVariant } from "../ui/DropdownMenu";

export const DatasetCard = ({
  datasetGroup,
  onClick,
  loadingState,
  tooltip,
}: {
  datasetGroup: DatasetGroup;
  onClick?: () => void;
  loadingState?: {
    progress: number;
    dataset_id: string;
  };
  tooltip?: string;
}) => {
  const auth = useAuth();
  const { mutateAsync: deleteDatasetGroup } = useDeleteDatasetGroup(
    auth?.userToken!,
  );

  const [searchParams] = useSearchParams();
  const { workspaceId } = useParams();
  const datasets = datasetGroup.datasets || [];
  const latestDataset = datasets[datasets.length - 1];
  const versionCount = datasets.filter((d) => d.version_number !== -1).length;
  const columnCount = (latestDataset as any)?.column_names?.length || 0;
  const createdAgo = moment(datasetGroup.created_at).fromNow();
  const isDraft = datasetGroup.datasets?.some((d) => d.version_number === -1);
  const displayColumns = (latestDataset as any)?.column_names || [];
  const metadata = [
    {
      text: `${versionCount} version${versionCount !== 1 ? "s" : ""}`,
      icon: CollectionIcon,
    },
    {
      text: `${columnCount} column${columnCount !== 1 ? "s" : ""}`,
      icon: TableIcon,
    },
    {
      text: createdAgo,
      icon: CalendarIcon,
      tooltip: `Created ${moment(datasetGroup.created_at).format(
        "MMMM D, YYYY [at] h:mm A",
      )}`,
    },
  ];
  const link = `/workspace/${workspaceId}/dataset-groups/${
    datasetGroup.id
  }${keepRelevantSearchParams(searchParams)}`;

  const actions = [
    {
      id: "delete-dataset",
      label: "Delete",
      onClick: () => deleteDatasetGroup(datasetGroup.id),
      icon: TrashIcon,
      variant: DropdownMenuVariant.DESTRUCTIVE,
    },
  ];

  return (
    <Card
      item={datasetGroup}
      link={link}
      actions={actions}
      loadingState={loadingState}
      key={datasetGroup.id}
      title={datasetGroup.name}
      emptyMetadataMessage="No metadata"
      emptyTagsMessage="No columns"
      variant={isDraft ? CardVariant.WARNING : CardVariant.DEFAULT}
      icon={
        <BasicTooltip
          shouldShow={isDraft || Boolean(loadingState)}
          className="cursor-pointer"
          content={tooltip}
        >
          <span>
            <Dataset className="h-5 w-5" />
          </span>
        </BasicTooltip>
      }
      metadata={metadata}
      tags={displayColumns}
      maxVisibleTags={3}
      tagsSeparator={CardTagsSeparator.PIPE}
      useBadge={false}
      onClick={onClick}
    />
  );
};
